import { useFormik } from 'formik';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import { DashboardFilterStore } from 'zustand-store';
import { DashboardSearchForm, DashboardSearchType, ReactTag } from 'types';
import { removeLocalStorageItem } from 'helpers';
import {
  LocalStorageKeys,
  TEST_IDS,
  getDefaultDashboardSearch,
  searchOptions,
} from 'commons';
import { Input, Select } from '../components';
import * as S from '../styles';

const DEFAULT_SEARCH_TYPE = 'codes';

export function DashboardSearch() {
  const location = useLocation();
  const { dashboardSearch, setDashboardSearch } =
    DashboardFilterStore.useStore();
  const [codeText, setCodeText] = useState<string>('');
  const [tags, setTags] = useState<ReactTag[]>([]);

  const firstRender = useRef(true);

  useEffect(() => {
    const { hash } = window.location;

    if (hash.includes('#')) {
      const consignmentCodes = hash.replace('#', '');
      const newTags = consignmentCodes.split(',').map((consignmentCode) => {
        return {
          id: consignmentCode,
          text: consignmentCode,
        };
      });

      setTags(newTags);

      setDashboardSearch({
        ...dashboardSearch,
        searchType: DashboardSearchType.Codes,
        codes: consignmentCodes,
        cpf: '',
        email: '',
        ean: '',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (firstRender.current) {
      const codesToTags = dashboardSearch.codes
        .split(',')
        .filter((value) => value !== '');

      if (codesToTags.length) {
        const initialTags: ReactTag[] = codesToTags.map((code) => {
          return {
            id: code,
            text: code,
          };
        });

        setTags(initialTags);
      }

      firstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  const { values, handleSubmit, setFieldValue, setValues } =
    useFormik<DashboardSearchForm>({
      initialValues: dashboardSearch,

      onSubmit: (values) => {
        const { searchType, email, cpf, ean } = values;
        const searchTags: ReactTag[] = tags;

        if (codeText !== '') {
          const closedTag: ReactTag = { id: codeText, text: codeText };
          setCodeText('');

          searchTags.push(closedTag);
          setTags(searchTags);
        }

        const submitCodes = searchTags.map((tag) => tag.text).join(',');

        const submitSearch: DashboardSearchForm = {
          searchType,
          codes: searchType === DashboardSearchType.Codes ? submitCodes : '',
          email: searchType === DashboardSearchType.Email ? email : '',
          cpf: searchType === DashboardSearchType.Cpf ? cpf : '',
          ean: searchType === DashboardSearchType.Ean ? ean : '',
          filterDate: new Date().toDateString(),
        };

        setDashboardSearch(submitSearch);
      },

      onReset: (_, { setValues }) => {
        removeLocalStorageItem(LocalStorageKeys.DashboardSearch);

        const defaultDashboardSearch = getDefaultDashboardSearch();
        setDashboardSearch(defaultDashboardSearch);
        setValues(defaultDashboardSearch);
        setTags([]);
        setCodeText('');
      },
    });

  useEffect(() => {
    if (values.filterDate !== dashboardSearch.filterDate) {
      setValues(dashboardSearch);
      setTags([]);
      setCodeText('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardSearch.filterDate]);

  useEffect(() => {
    const defaultDashboardSearch = getDefaultDashboardSearch();

    if (isEqual(defaultDashboardSearch, dashboardSearch)) {
      const { codes, cpf, email, ean, searchType } = values;
      const isSearchModified =
        tags.length ||
        codeText ||
        codes ||
        cpf ||
        email ||
        ean ||
        searchType !== DEFAULT_SEARCH_TYPE;

      if (isSearchModified) {
        setValues(defaultDashboardSearch);
        setTags([]);
        setCodeText('');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardSearch]);

  function changeSearchType(value: string) {
    setTags([]);
    setCodeText('');
    setFieldValue('searchType', value);
    setFieldValue('codes', '');
    setFieldValue('email', '');
    setFieldValue('cpf', '');
    setFieldValue('ean', '');

    handleSubmit();
  }
  const { searchType } = values;

  function changeInputValue(value: string) {
    if (searchType === DEFAULT_SEARCH_TYPE) {
      setCodeText(value);
    } else {
      setFieldValue(searchType, value);
    }
  }

  const inputValue =
    searchType === DEFAULT_SEARCH_TYPE ? codeText : values[searchType];

  return (
    <S.Form
      autoComplete="off"
      data-testid={TEST_IDS.CONSIGNMENT_SEARCH_BAR}
      onSubmit={handleSubmit}
    >
      <Select
        id="searchType"
        value={searchType}
        handleChange={changeSearchType}
        options={searchOptions}
      />

      <Input
        id={searchType}
        value={inputValue}
        handleChange={changeInputValue}
        handleSubmit={handleSubmit}
        tags={tags}
        setTags={setTags}
      />

      <S.Button type="submit" aria-label="pesquisar">
        <MdSearch />
      </S.Button>
    </S.Form>
  );
}
